/**
 * Sysadmin Platform — Tenants list (Wave 1a / W1a-17).
 *
 * Lists every tenant on the platform with derived status (active /
 * suspended / deleted / pending) and live user counts. Filter chips
 * (status), search (name/slug/orgCode), and a tap-to-detail action that
 * navigates to W1a-18's tenant detail page (placeholder until W1a-18
 * lands).
 */

import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Building2, Search, Plus, Filter, ArrowRight,
  CheckCircle2, AlertOctagon, Clock, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useTenants, type TenantStatus } from '@/lib/use-tenants';
import { cn } from '@/lib/utils';
import { ProvisionTenantWizard } from '@/components/platform/provision-tenant-wizard';

export const Route = createFileRoute('/sysadmin/tenants/')({
  component: PlatformTenantsPage,
});

const STATUS_CHIPS: Array<{ key: TenantStatus | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'pending', label: 'Pending' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'deleted', label: 'Deleted' },
];

function PlatformTenantsPage() {
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<TenantStatus | 'all'>('all');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);

  // Debounce search to avoid hammering the API per keystroke.
  useMemo(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, error } = useTenants({
    q: debouncedSearch.trim() || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            Tenants
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Platform-wide tenant roster. New tenants are provisioned via the
            Tenant wizard. Click a row to open tenant detail.
          </p>
        </div>
        <Button onClick={() => setWizardOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New tenant
        </Button>
      </div>

      <ProvisionTenantWizard open={wizardOpen} onOpenChange={setWizardOpen} />

      {/* Filter bar */}
      <Card>
        <CardContent className="py-4 flex flex-col md:flex-row gap-3 items-start md:items-center">
          <div className="relative flex-1 w-full">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, slug, or code…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Filter className="h-4 w-4 text-muted-foreground mr-1" />
            {STATUS_CHIPS.map((chip) => (
              <Button
                key={chip.key}
                size="sm"
                variant={statusFilter === chip.key ? 'default' : 'outline'}
                onClick={() => setStatusFilter(chip.key)}
                className="h-7 text-xs"
              >
                {chip.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tenants table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>
            {isLoading ? 'Loading…' : `${data?.total ?? 0} tenant${data?.total === 1 ? '' : 's'}`}
          </CardTitle>
          <CardDescription>
            Click a tenant row to open its detail page.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          {error && (
            <div className="px-6 py-4 text-sm text-destructive">
              Failed to load tenants: {error.message}
            </div>
          )}
          {!error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Users</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      Loading…
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && data?.tenants.length === 0 && (
                  // Issue #931 — mark the empty-state row so tests can
                  // disambiguate "first row" assertions from the message
                  // copy. Without this attribute, SYS-TENANTS-006
                  // (Suspended filter empty result) reads the message as
                  // if it were a real row's textContent.
                  <TableRow data-empty-state="true">
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      No tenants match the current filters.
                    </TableCell>
                  </TableRow>
                )}
                {data?.tenants.map((t) => (
                  <TableRow key={t.id} className="hover:bg-accent/40">
                    <TableCell className="font-medium">
                      {t.name}
                      {t.isRoot && (
                        <Badge variant="outline" className="ml-2 text-xs">Root</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {t.slug}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {t.orgCode}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={t.status} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{t.userCount}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(t.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/sysadmin/tenants/$tenantId"
                        params={{ tenantId: t.id }}
                        className="text-muted-foreground hover:text-foreground inline-flex items-center"
                        title="Open tenant detail"
                      >
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Status badge ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: TenantStatus }) {
  const config = {
    active:    { label: 'Active',    icon: CheckCircle2, cls: 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30' },
    pending:   { label: 'Pending',   icon: Clock,        cls: 'text-amber-700 bg-amber-100 dark:text-amber-400 dark:bg-amber-900/30' },
    suspended: { label: 'Suspended', icon: AlertOctagon, cls: 'text-orange-700 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30' },
    deleted:   { label: 'Deleted',   icon: Trash2,       cls: 'text-rose-700 bg-rose-100 dark:text-rose-400 dark:bg-rose-900/30' },
  }[status];
  const Icon = config.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium', config.cls)}>
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
}
